import { EventEmitter, Injectable } from '@angular/core';
import { AbstractControl, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import moment, * as _moment from 'moment';
import { LeaveService } from 'app/modules/leave/services/leave.service';

@Injectable({
    providedIn: 'root',
})
export class LeaveValidatorService {
    public validationForm: UntypedFormGroup;
    private leaveListForValidations: Array<any>;
    public leaveListUpdated = new EventEmitter(false);

    constructor(private leaveService: LeaveService) {
        const EmployeeId = localStorage.getItem('EmplooyeeID');
        this.updateLeaveList(Number(EmployeeId));
    }

    public updateLeaveList(employeeId: number): void {
        this.leaveListForValidations = null;
        this.leaveService
            .GetLeaveListForValidations(String(employeeId))
            .subscribe((res) => {
                if (res.code === 0) {
                    this.leaveListForValidations = res.result;
                    this.leaveListUpdated.emit(true);
                }
            });
    }

    public leaveDateAndDischargedDateValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }
            const leaveDate: _moment.Moment =
                this.validationForm.get('leaveDate').value;
            const dischargedDate: _moment.Moment = control.value;
            if (leaveDate === null || dischargedDate === null) {
                return null;
            }
            const difference = leaveDate.diff(dischargedDate, 'days');
            const forbidden = difference > 0;
            return forbidden
                ? {
                      NoEarlyDischargedDateOverLeaveDate: {
                          value: control.value,
                          difference: difference,
                      },
                  }
                : null;
        };
    }

    public leaveDateAndCommunicationDateValidation(): ValidatorFn {
        return (): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }
            const leaveDate: _moment.Moment =
                this.validationForm.get('leaveDate').value;
            const communicationDate: _moment.Moment =
                this.validationForm.get('leaveCommunication').value;
            if (leaveDate === null || communicationDate === null) {
                return null;
            }
            const difference = leaveDate.diff(communicationDate, 'days');
            const forbidden = difference > 0;
            return forbidden
                ? {
                      NoEarlyCommunicationDateOverLeaveDate: {
                          value: communicationDate,
                          difference: difference,
                      },
                  }
                : null;
        };
    }

    public dischargedDateAndDischargedCommunicationDateValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }
            const dischargedDate: _moment.Moment =
                this.validationForm.get('dischargeDate').value;
            const communicationDate: _moment.Moment = control.value;
            if (dischargedDate === null || communicationDate === null) {
                return null;
            }
            const difference = dischargedDate.diff(communicationDate, 'days');
            const forbidden = difference > 0;
            return forbidden
                ? {
                      NoEarlyCommunicationDateOverDischargedDate: {
                          value: control.value,
                          difference: difference,
                      },
                  }
                : null;
        };
    }

    public dischargedCommunicationDateAndDischargedDateValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }
            const dischargedCommunicationDate: _moment.Moment =
                this.validationForm.get('dischargeCommunication').value;
            const dischargedDate: _moment.Moment =
                this.validationForm.get('dischargeDate').value;
            if (
                dischargedCommunicationDate === null ||
                dischargedDate === null
            ) {
                return null;
            }
            const forbidden =
                dischargedCommunicationDate.isBefore(dischargedDate);
            return forbidden
                ? {
                      NoHigherDischargedDateOverCommunicationDate: {
                          value: control.value,
                      },
                  }
                : null;
        };
    }

    public leaveDateAndExpectedDischargedDateValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!this.validationForm) {
                return null;
            }
            
            if (!control.dirty && !control.touched) {
                return null;
            }
            
            const leaveDate = this.validationForm.get('leaveDate')?.value;
            const expectedDischargedDate = control.value;
    
            if (!leaveDate || !expectedDischargedDate) {
                return null;
            }
    
            const today = moment().startOf('day'); 
            const isBeforeToday = moment(expectedDischargedDate).isBefore(today);
            const isExceeding = moment(expectedDischargedDate).diff(moment(leaveDate), 'days') > 545;
    
            if (isBeforeToday) {
                return { NoEarlyExpectedDischargedDateOverDischargedDate: { value: control.value} };
            } 
            if (isExceeding) {
                return { NoExceedingExpectedDischargedDate: { value: control.value} };
            }
    
            return null;
        };
    }

    public sameLeaveDateValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            if (
                !this.leaveListForValidations ||
                (this.leaveListForValidations &&
                    this.leaveListForValidations.length === 0)
            ) {
                return null;
            }

            const leaveDate: _moment.Moment =
                this.validationForm.get('leaveDate').value;

            if (leaveDate === null) {
                return null;
            }

            const conflictLeaves = this.leaveListForValidations.filter(
                (e) =>
                    e.Fbaja &&
                    leaveDate.format('DD-MM-YYYY') ===
                        _moment(e.Fbaja).format('DD-MM-YYYY')
            );

            const invalid = conflictLeaves.length > 0;

            return invalid
                ? { SameLeaveDateExists: { value: control.value } }
                : null;
        };
    }

    public isLeaveDateAfterLastDischargeValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            if (
                !this.leaveListForValidations ||
                (this.leaveListForValidations &&
                    this.leaveListForValidations.length === 0)
            ) {
                return null;
            }

            const dischargeDate: _moment.Moment =
                this.validationForm.get('dischargeDate').value;
            if (dischargeDate) {
                return null;
            }

            const leaveDate: _moment.Moment =
                this.validationForm.get('leaveDate').value;
            if (!leaveDate) {
                return null;
            }

            const lastLeave = this.leaveListForValidations[0];
            const invalid =
                lastLeave.Falta && _moment(lastLeave.Falta) > leaveDate;

            return invalid
                ? { LeaveNotAfterLastDischarge: { value: control.value } }
                : null;
        };
    }

    public leaveCommunicationRequiredValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            const dischargeDate: _moment.Moment =
                this.validationForm.get('dischargeDate').value;
            const leaveCommunication: _moment.Moment =
                this.validationForm.get('leaveCommunication').value;

            return !dischargeDate && !leaveCommunication
                ? { LeaveCommunicationDateRequired: { value: control.value } }
                : null;
        };
    }

    public dischargeCommunicationRequiredValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            const dischargeDate: _moment.Moment =
                this.validationForm.get('dischargeDate').value;
            const dischargeCommunication: _moment.Moment =
                this.validationForm.get('dischargeCommunication').value;

            return dischargeDate && !dischargeCommunication
                ? {
                      DischargeCommunicationDateRequired: {
                          value: control.value,
                      },
                  }
                : null;
        };
    }

    public communicationDaysValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return;
            }

            const dischargeCommunication: _moment.Moment =
                this.validationForm.get('dischargeCommunication').value;
            if (!dischargeCommunication) {
                return;
            }

            const leaveCommunication: _moment.Moment =
                this.validationForm.get('leaveCommunication').value;
            if (dischargeCommunication.isBefore(leaveCommunication, 'day')) {
                return {
                    DischargeCommunicationDateBeforeLeaveCommunication: {
                        value: control.value,
                    },
                };
            }

            return;
        };
    }

    public leaveDischargeOverlapValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            if (
                !this.leaveListForValidations ||
                (this.leaveListForValidations &&
                    this.leaveListForValidations.length === 0)
            ) {
                return null;
            }

            const leaveDate: _moment.Moment =
                this.validationForm.get('leaveDate').value;
            if (!leaveDate) {
                return null;
            }

            const dischargeDate: _moment.Moment =
                this.validationForm.get('dischargeDate').value;
            if (!dischargeDate) {
                return null;
            }

            // Check that the period (FBaja..FAlta) is overlapping any other leave of the
            // employee. If it is “La baja se solapa con otra del mismo trabajador”

            const dischargeDateInPeriod = this.leaveListForValidations.filter(
                (e) =>
                    e.Fbaja &&
                    e.Falta &&
                    dischargeDate.isBetween(_moment(e.Fbaja), _moment(e.Falta))
            );

            const leavesInNewPeriod = this.leaveListForValidations.filter(
                (e) =>
                    e.Fbaja &&
                    _moment(e.Fbaja).isBetween(leaveDate, dischargeDate)
            );
            const dischargesInNewPeriod = this.leaveListForValidations.filter(
                (e) =>
                    e.Falta &&
                    _moment(e.Falta).isBetween(leaveDate, dischargeDate)
            );

            if (
                dischargeDateInPeriod.length > 0 ||
                leavesInNewPeriod.length > 0 ||
                dischargesInNewPeriod.length > 0
            ) {
                return { LeavePeriodConflict: { value: control.value } };
            }

            // In Order To Reset the Open Leave Validation
            this.validationForm.get('leaveDate').patchValue(leaveDate);
            return null;
        };
    }

    public leaveOverlapValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            if (
                !this.leaveListForValidations ||
                (this.leaveListForValidations &&
                    this.leaveListForValidations.length === 0)
            ) {
                return null;
            }

            const leaveDate: _moment.Moment =
                this.validationForm.get('leaveDate').value;
            if (!leaveDate) {
                return null;
            }

            // Check that the period (FBaja..FAlta) is overlapping any other leave of the
            // employee. If it is “La baja se solapa con otra del mismo trabajador”

            const leavesDateInPeriod = this.leaveListForValidations.filter(
                (e) =>
                    e.Fbaja &&
                    e.Falta &&
                    leaveDate.isBetween(_moment(e.Fbaja), _moment(e.Falta))
            );

            if (leavesDateInPeriod.length > 0) {
                return { LeavePeriodConflict: { value: control.value } };
            }

            return null;
        };
    }

    public communicationResetValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (this.validationForm) {
                const leaveCommunication: _moment.Moment =
                    this.validationForm.get('leaveCommunication').value;
                const dischargeCommunication: _moment.Moment =
                    this.validationForm.get('dischargeCommunication').value;
                this.validationForm
                    .get('leaveCommunication')
                    .patchValue(leaveCommunication);
                this.validationForm
                    .get('dischargeCommunication')
                    .patchValue(dischargeCommunication);
                return null;
            }
        };
    }

    public openLeaveOverlapValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            if (
                !this.leaveListForValidations ||
                (this.leaveListForValidations &&
                    this.leaveListForValidations.length === 0)
            ) {
                return null;
            }

            const leaveDate: _moment.Moment =
                this.validationForm.get('leaveDate').value;
            if (!leaveDate) {
                return null;
            }

            const dischargeDate: _moment.Moment =
                this.validationForm.get('dischargeDate').value;
            if (dischargeDate) {
                return null;
            }

            const openLeaves = this.leaveListForValidations.filter(
                (e) => e.Fbaja && !e.Falta
            );

            return openLeaves.length > 0
                ? { OpenLeavePeriodConflict: { value: control.value } }
                : null;
        };
    }

    public gestionDateBetweenLeaveCommunicationAndTodayValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm || !control.value) {
                return null;
            }

            const communicationDate: moment.Moment =
                this.validationForm.get('leaveCommunication').value;
            if (!communicationDate) {
                return null;
            }

            const forbidden =
                control.value.isBefore(communicationDate) ||
                control.value.isAfter(moment());
            return forbidden
                ? {
                      GestionDateShouldBetweenCommunicationAndToday: {
                          value: control.value,
                      },
                  }
                : null;
        };
    }

    // Only for leave editing validations
    public hasOpenLeaveValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            const dischargeDate: _moment.Moment =
                this.validationForm.get('dischargeDate').value;
            if (dischargeDate) {
                return null;
            }

            if (
                !this.leaveListForValidations ||
                (this.leaveListForValidations &&
                    this.leaveListForValidations.length === 0)
            ) {
                return null;
            }

            const leaveId: number = this.validationForm.get('leaveId').value;
            const openLeaves = this.leaveListForValidations.filter(
                (e) => e.Fbaja && !e.Falta && e.id !== String(leaveId)
            );

            return openLeaves.length > 0
                ? { HasAnotherOpenLeave: { value: control.value } }
                : null;
        };
    }

    public sameLeaveDateEditValidation(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!this.validationForm) {
                return null;
            }

            if (
                !this.leaveListForValidations ||
                (this.leaveListForValidations &&
                    this.leaveListForValidations.length === 0)
            ) {
                return null;
            }

            const leaveId: number = this.validationForm.get('leaveId').value;
            const leaveDate: _moment.Moment =
                this.validationForm.get('leaveDate').value;

            if (!leaveDate || !leaveId) {
                return null;
            }

            const conflictLeaves = this.leaveListForValidations.filter(
                (e) =>
                    e.Fbaja &&
                    e.id !== String(leaveId) &&
                    leaveDate.format('DD-MM-YYYY') ===
                        _moment(e.Fbaja).format('DD-MM-YYYY')
            );
            const invalid = conflictLeaves.length > 0;

            return invalid
                ? { SameLeaveDateExists: { value: control.value } }
                : null;
        };
    }

    private compareValues(key, order = 'asc'): any {
        return (a, b) => {
            if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
                // property doesn't exist on either object
                return 0;
            }

            const varA =
                typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
            const varB =
                typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];

            let comparison = 0;
            if (varA > varB) {
                comparison = 1;
            } else if (varA < varB) {
                comparison = -1;
            }
            return order === 'desc' ? comparison * -1 : comparison;
        };
    }
}
